import { useMemo } from "react";
import type { EntityItemConfig } from "@salestrak/ui";
import {
  useMemberItemsWith,
  useTeamItemsWith,
} from "#app/components/entity-selectors";
import {
  UNASSIGNED_GROUP_KEY,
  type IssueGroupDimension,
} from "#app/components/issues/issue-grouping";

const NO_KEYS: string[] = [];

/**
 * `memberItems` (the roster) and `teamItems`, each widened by every key the group
 * counts name for the active dimension — both lists load one page, so a
 * member or team past it would otherwise get no group. `countedItems` is the
 * active dimension's list, or null for a dimension over neither.
 */
export function useCountedGroupItems(
  dimension: IssueGroupDimension | undefined,
  counts: Record<string, number>,
  memberItems: EntityItemConfig[],
  teamItems: EntityItemConfig[],
) {
  const isTeamDimension = dimension === "team" || dimension === "teams";
  const countedKeys = useMemo(
    () => Object.keys(counts).filter((key) => key !== UNASSIGNED_GROUP_KEY),
    [counts],
  );
  const allMemberItems = useMemberItemsWith(
    memberItems,
    dimension === "assignee" ? countedKeys : NO_KEYS,
  );
  const allTeamItems = useTeamItemsWith(teamItems, isTeamDimension ? countedKeys : NO_KEYS);
  return {
    memberItems: allMemberItems,
    teamItems: allTeamItems,
    countedItems:
      dimension === "assignee" ? allMemberItems : isTeamDimension ? allTeamItems : null,
  };
}
